<template>
  <div
    class="base-switch"
    :class="{ 'base-switch--disabled': isInteractionBlocked }"
  >
    <input
      :checked="checked"
      type="checkbox"
      class="base-switch__control"
      :disabled="isInteractionBlocked"
      @change="handleChange"
      @click="handleClick"
    >
    <div class="base-switch__circle" :class="{ 'base-switch__circle--loading': props.loading }">
      <BaseLoader
        v-if="props.loading"
        size="16px"
        color="var(--color-foreground-inverted)"
        :stroke-width="8"
      />
    </div>
    <div class="base-switch__background" />
  </div>
</template>

<script setup lang="ts">
import BaseLoader from '../loader/index.vue'

interface Props {
  modelValue: boolean
  loading?: boolean
  disabled?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const checked = computed({
  get() {
    return props.modelValue
  },
  set(value: boolean) {
    emit('update:modelValue', value)
  },
})

const isInteractionBlocked = computed(() => props.disabled || props.loading)

function handleClick(event: MouseEvent) {
  if (isInteractionBlocked.value) {
    event.preventDefault()
    event.stopPropagation()
  }
}

function handleChange(event: Event) {
  if (isInteractionBlocked.value) {
    return
  }

  checked.value = (event.target as HTMLInputElement).checked
}
</script>

<style lang="scss" scoped>
.base-switch {
  width: 36px;
  height: 20px;
  position: relative;
  background-color: var(--color-surface-placeholder-900);
  border-radius: 10px;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;

  &__control {
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__circle {
    position: absolute;
    z-index: 10;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 16px;
    background-color: var(--color-surface-bg);
    pointer-events: none;
    transition: all 0.25s ease;

    &--loading {
      background-color: transparent;
    }
  }

  &__background {
    position: absolute;
    left: -100%;
    z-index: 2;
    width: 100%;
    height: auto;
    background-color: var(--color-surface-primary);
    padding-bottom: 100%;
    transition: all 0.25s ease-out;
  }

  &__control:checked ~ &__circle {
    left: calc(100% - 18px);
  }

  &__control:checked ~ &__background {
    left: 0;
    transition: all 0.25s ease;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
